<script setup lang="ts">
import type { MaxBid, ProductDetails } from "mws-ts-v2-sdk";

import { BidAttemptType, BidModalEnum } from "~/types/Catalog";

type Props = {
  type: BidModalEnum;
  activeMaxBid?: MaxBid | null;
  product: ProductDetails;
  bidAttempt: {
    loading: boolean;
    amount: number;
    type?: BidAttemptType;
    currency: string;
  };
  paymentTokenId?: string;
};

const { type } = defineProps<Props>();

type Emits = {
  (e: "close"): void;
  (e: "submit"): void;
  (e: "continue-with-payment-token", paymentTokenId?: string): void;
  (e: "back-to-card-overview"): void;
  (e: "raise-bid"): void;
};

defineEmits<Emits>();

const { bidVerified } = useBidder();

const bidPlacedModalOpen = computed(() => {
  const bidPlacedModalTypes = [
    BidModalEnum.Placed,
    BidModalEnum.Outbid,
    BidModalEnum.PlacedExtended,
    BidModalEnum.OutbidExtended,
  ];

  return bidPlacedModalTypes.includes(type);
});
</script>

<template>
  <UserAddressRequiredModal
    v-if="type === BidModalEnum.AddressRequired"
    @close="$emit('close')"
  />
  <ProductBidModalLimitWarning
    v-else-if="type === BidModalEnum.LimitWarning"
    @close="$emit('close')"
  />
  <ProductBidModalPlaced
    v-else-if="bidPlacedModalOpen"
    :data-cy="`${type}-modal`"
    :is-outbid="
      type === BidModalEnum.Outbid || type === BidModalEnum.OutbidExtended
    "
    :is-extended="
      type === BidModalEnum.OutbidExtended ||
      type === BidModalEnum.PlacedExtended
    "
    :end-date="product.endDate"
    :active-max-bid="activeMaxBid"
    :prices="product.prices"
    @close="$emit('close')"
  />
  <ProductBidModalConfirm
    v-else-if="bidVerified && type === BidModalEnum.Confirm"
    :product="product"
    :amount-in-active-currency="bidAttempt.amount"
    :bid-attempt="bidAttempt.type"
    :is-bid-loading="bidAttempt.loading"
    :payment-token-id="paymentTokenId"
    @confirm="$emit('submit')"
    @close="$emit('close')"
  />
  <ProductBidModalReservationRequired
    v-else-if="bidVerified && type === BidModalEnum.ReservationRequired"
    :product="product"
    :amount-in-active-currency="bidAttempt.amount"
    :bid-type="bidAttempt.type"
    @close="$emit('close')"
    @continue="(tokenId) => $emit('continue-with-payment-token', tokenId)"
  />
  <ProductBidModalRaiseBid
    v-else-if="type === BidModalEnum.RaiseBid"
    :amount-in-active-currency="bidAttempt.amount"
    :currency="bidAttempt.currency"
    :bid-attempt="bidAttempt.type"
    :product="product"
    @close="$emit('close')"
    @back-to-card-overview="$emit('back-to-card-overview')"
    @raise-bid="$emit('raise-bid')"
  />
</template>
